import React, {useState} from 'react';
import {Drawer} from '../Drawer';
import {Checkbox, FormControlLabel, Link, Stack, Typography, useTheme} from '@mui/material';
import {translations} from 'presentation/translations-new';
import {useVoiceTranscriptionDrawerStore} from './store';
import {useGetEndpointUrl, useHealthEmergencyLinkFactory} from 'presentation/hooks';
import {ModalError} from '../ModalError';
import {TERMS_URL} from './constants';
import {useShallow} from 'zustand/shallow';
import {usePostVoiceTranscriptionTermsAcceptMutation} from './mutation/usePostVoiceTranscriptionTermsAcceptMutation';

const {content, description, footer, title} =
	translations['pt-br'].privateFlow.voiceTranscriptionDrawer;
const {keep, requiredField} = translations['pt-br'].commons;
const {emergencyTelehealth} = translations['pt-br'].privateFlow.services;
const {
	title: modalErrorTitle,
	subtitle,
	description: modalErrorDescription,
	tryAgainBtn,
	backAction,
} = translations['pt-br'].others.genericErrorModal;

export function VoiceTranscriptionDrawer() {
	const theme = useTheme();
	const {isOpen, onClose, onOpen, telehealthUrl, origin, onError, onSuccess} =
		useVoiceTranscriptionDrawerStore(
			useShallow(state => ({
				isOpen: state.isOpen,
				onClose: state.onClose,
				onOpen: state.onOpen,
				telehealthUrl: state.telehealthUrl,
				origin: state.origin,
				onError: state.onError,
				onSuccess: state.onSuccess,
			})),
		);
	const {getLink, isError, setIsError, isLoading} = useGetEndpointUrl(emergencyTelehealth);
	const makeHealthEmergencyLink = useHealthEmergencyLinkFactory();
	const {mutateAsync: acceptTerms, isPending: isAcceptingTerms} =
		usePostVoiceTranscriptionTermsAcceptMutation();
	const [isChecked, setIsChecked] = useState(false);
	const [touched, setTouched] = useState(false);

	const hasError = touched && !isChecked;

	const handleClose = () => {
		setIsChecked(false);
		setTouched(false);
		setIsError(false);
		onClose();
	};

	const handleCheck = () => {
		if (touched) {
			setTouched(false);
		}
		setIsChecked(prev => !prev);
	};

	const handleSubmit = async () => {
		setTouched(true);

		if (!isChecked) return;

		try {
			await acceptTerms({Origem: origin ?? ''});

			if (telehealthUrl) {
				window.open(telehealthUrl);
				handleClose();
				onSuccess?.();
				return;
			}

			const success = await getLink(makeHealthEmergencyLink);
			if (success === null) {
				handleClose();
				return;
			}

			if (!success) {
				if (onError) {
					handleClose();
					onError();
				}
				return;
			}

			handleClose();
			onSuccess?.();
		} catch (error) {
			if (onError) {
				handleClose();
				onError();
				return;
			}
			setIsError(true);
		}
	};

	const handleTryAgain = () => {
		setIsError(false);
		setIsChecked(true);
		onOpen(telehealthUrl, origin, onError, onSuccess);
	};

	return (
		<>
			<Drawer.Root
				open={isOpen}
				onClose={handleClose}
				onOpen={() => onOpen(telehealthUrl, origin, onError, onSuccess)}
				width="420px"
				customMaxHeight={'90%'}
				disableSwipeToOpen>
				<Drawer.Header title={title} fontSize={'18px'} onClose={handleClose} />
				<Drawer.Content>
					<Stack gap={2}>
						<Typography variant="h6" mt={2}>
							{description}
						</Typography>
						<Typography variant="body1">{content.paragraph1}</Typography>
						<Typography variant="body1">{content.paragraph2}</Typography>
						<Typography variant="body1">{content.paragraph3}</Typography>
					</Stack>
				</Drawer.Content>
				<Drawer.Footer
					secondButtonTitle={keep}
					hideFirstButton
					secondButtonOnClick={() => handleSubmit()}
					secondButtonIsLoading={isLoading || isAcceptingTerms}
					secondButtonDisabled={isLoading || isAcceptingTerms}
					canStacks>
					<Stack direction="column" gap={1}>
						<FormControlLabel
							control={
								<Checkbox
									checked={isChecked}
									onChange={handleCheck}
									sx={{
										py: 0,
										ml: 1,
										color: hasError ? theme.palette.error.main : null,
									}}
								/>
							}
							label={
								<Typography
									variant="body1"
									color={
										hasError
											? theme.palette.error.main
											: theme.palette.text.primary
									}>
									{footer.label}{' '}
									<Link
										href={TERMS_URL}
										sx={{
											color: theme.palette.primary.main,
											textDecorationColor: theme.palette.primary.main,
										}}
										target="_blank"
										rel="noopener noreferrer">
										{footer.link}
									</Link>
								</Typography>
							}
						/>
						{hasError && (
							<Typography
								variant="caption"
								color={theme.palette.error.main}
								sx={{ml: 5, mt: -1}}>
								{requiredField}
							</Typography>
						)}
					</Stack>
				</Drawer.Footer>
			</Drawer.Root>

			{isError && !onError ? (
				<ModalError
					open={isError}
					onClose={() => setIsError(false)}
					headerTitle={modalErrorTitle}
					title={subtitle}
					isFullScreen={false}
					maxWidth={420}
					description={modalErrorDescription}
					firstButton={{
						title: tryAgainBtn,
						handleClick: () => handleTryAgain(),
						variant: 'contained',
					}}
					secondButton={{
						title: backAction,
						handleClick: () => handleClose(),
						variant: 'outlined',
					}}
				/>
			) : null}
		</>
	);
}
